// kanjo/Vendor.jsx — Vendor detail wired to live performance metrics (US-2.2).
// Style aligned with the mockup design system: vendor avatar header + 4-up KPI
// tile row + section cards.

function fmtMoneyV(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' })
    .format((c || 0) / 100);
}
function fmtPctV(p) { return p == null ? '—' : `${(p * 100).toFixed(0)}%`; }

// ── KpiTile: one of four metric tiles arranged in a row ───────────────────
function KpiTile({ label, value, sub, color, last }) {
  return (
    <div style={{
      flex: 1, padding: '14px 14px',
      borderRight: last ? 'none' : '1px solid var(--rule)',
    }}>
      <div className="label">{label}</div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 600, marginTop: 6, color: color || 'var(--ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function vendorInitial(name) {
  const trimmed = (name || '?').trim();
  return trimmed[0]?.toUpperCase() || '?';
}

// 13-week spend chart for the past 90 days. Buckets invoices by the ISO week
// (Mon → Sun) of their issuedDate; the current (week-to-date) bar is faded.
function VendorWeeklySpend({ invoices, currency, locale }) {
  const weeks = React.useMemo(() => {
    // Start of this week (Monday, UTC).
    const t = new Date();
    const dow = (t.getUTCDay() + 6) % 7; // 0 = Monday
    const thisMon = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate() - dow));
    const w = [];
    for (let i = 12; i >= 0; i--) {
      const start = new Date(thisMon); start.setUTCDate(thisMon.getUTCDate() - i * 7);
      const end = new Date(start);     end.setUTCDate(start.getUTCDate() + 7);
      w.push({ startIso: start.toISOString().slice(0, 10), endIso: end.toISOString().slice(0, 10), cents: 0, partial: i === 0 });
    }
    for (const inv of (invoices || [])) {
      const d = (inv.issuedDate || '').slice(0, 10);
      if (!d) continue;
      for (const wk of w) if (d >= wk.startIso && d < wk.endIso) { wk.cents += inv.totalCents || 0; break; }
    }
    return w;
  }, [invoices]);
  const max = Math.max(1, ...weeks.map((wk) => wk.cents));
  const total = weeks.reduce((s, wk) => s + wk.cents, 0);
  if (total === 0) return null;
  const fmt = (c) => fmtMoneyV(c, currency, locale);
  const monLabel = (iso) => new Date(iso + 'T00:00:00Z').toLocaleDateString(locale || 'en-US',
    { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return (
    <div style={{ padding: '0 20px 14px' }}>
      <div className="card" style={{ padding: 16 }}>
        <div className="label" style={{ marginBottom: 10 }}>Weekly spend · last 90 days</div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 84 }}>
          {weeks.map((wk) => {
            const h = wk.cents > 0 ? Math.max(3, Math.round((wk.cents / max) * 76)) : 2;
            return (
              <div key={wk.startIso} title={`Week of ${monLabel(wk.startIso)} — ${fmt(wk.cents)}${wk.partial ? ' (week-to-date)' : ''}`}
                style={{ flex: 1, height: h, borderRadius: 3,
                  background: wk.cents > 0 ? 'var(--brand,#5b8def)' : 'var(--surface-3)',
                  opacity: wk.partial ? 0.55 : 1 }}/>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
          <span>week of {monLabel(weeks[0].startIso)}</span>
          <span>this week</span>
        </div>
      </div>
    </div>
  );
}

function useVendorDetail() {
  // Read the id reactively from the current route. The router matches
  // "/buy/vendor" for every "?id=..." (query is stripped before matching), so
  // React reuses this component across vendors — a one-shot useEffect([]) would
  // pin the first vendor loaded. Keying off nav.current re-fetches on each id.
  const nav = React.useContext(KanjoNav);
  const route = (nav && nav.current) || '';
  const vendorId = React.useMemo(() => {
    // Read ?id from nav.current first; fall back to the raw hash in case the
    // context value lags a tick behind window.location.
    const hash = (window.location.hash || '').replace(/^#/, '');
    for (const src of [route, hash]) {
      if (src && src.includes('?')) {
        const v = new URLSearchParams(src.split('?')[1]).get('id');
        if (v) return v;
      }
    }
    return null;
  }, [route]);

  const [data, setData] = React.useState({ loading: true });
  React.useEffect(() => {
    let cancelled = false;
    setData({ loading: true });
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { setData({ loading: false }); return; }
        const profile = await window.kanjo.db.userProfile.get(claims.sub);
        let vendor = null;
        if (vendorId) {
          // A specific vendor was requested: load exactly that one. Do NOT fall
          // back to some other vendor — silently showing a different vendor than
          // the one tapped risks editing/paying the wrong supplier.
          vendor = await window.kanjo.db.vendors.get(vendorId);
        } else {
          // No id (e.g. the bare /buy/vendor entry): land on the first vendor.
          const list = await window.kanjo.procurement.vendors.listActive();
          vendor = list[0];
        }
        if (cancelled) return;
        if (!vendor) { setData({ loading: false, notFound: !!vendorId, requestedId: vendorId, route }); return; }
        const metrics = await window.kanjo.procurement.vendors.performanceMetrics(vendor.vendorId);
        if (!cancelled) setData({
          loading: false, vendor, metrics,
          locale: profile?.locale || 'en-US',
          currency: profile?.currency || 'USD',
        });
      } catch (e) {
        console.warn('[VendorDetail] load failed', e);
        if (!cancelled) setData({ loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId]);
  return data;
}

function VendorDetail({ dark = false, persona = 'owner' }) {
  const d = useVendorDetail();
  // Editable fields + payment policy (hooks must run before any early return).
  const [name, setName] = React.useState('');
  const [method, setMethod] = React.useState(null);
  const [offset, setOffset] = React.useState(5);
  const [matchBy, setMatchBy] = React.useState(null);   // 'amount' | 'label'
  const [matchLabel, setMatchLabel] = React.useState('');
  // Order contacts — where Send-cart in the shopping list will text/email to.
  const [orderSms, setOrderSms] = React.useState('');
  const [orderEmail, setOrderEmail] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const [modal, setModal] = React.useState(null);       // 'pay' | 'identify'
  const [invoices, setInvoices] = React.useState(null);
  const [dup, setDup] = React.useState(null);            // { other, survivorId, busy } | null

  React.useEffect(() => {
    if (d.vendor) {
      // Seeded with the DISPLAY name: in demo mode this field would otherwise be
      // the one place on the screen still printing the real supplier. saveName()
      // refuses to write while demo mode is on, so the fake can never be saved.
      setName(vendorName(d.vendor.name, ''));
      setMethod(d.vendor.paymentMethod || null);
      setOffset(d.vendor.statementPayOffsetDays ?? 5);
      setMatchBy(d.vendor.paymentMatch || null);
      setMatchLabel(d.vendor.paymentLabel || '');
      setOrderSms(d.vendor.orderSms || '');
      setOrderEmail(d.vendor.orderEmail || '');
    }
  }, [d.vendor]);

  // This vendor's invoices — drives the payments list + the 90-day average.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!d.vendor || !window.kanjo) { setInvoices(null); return; }
      try {
        const all = await window.kanjo.db.invoices.where('vendorId').equals(d.vendor.vendorId).toArray();
        all.sort((a, b) => (b.issuedDate || '').localeCompare(a.issuedDate || ''));
        if (!cancelled) setInvoices(all.filter((i) => !i.isDuplicate));
      } catch (e) { if (!cancelled) setInvoices([]); }
    })();
    return () => { cancelled = true; };
  }, [d.vendor && d.vendor.vendorId]);

  // Possible-duplicate detection: does another vendor look like the same supplier
  // under a slightly different OCR'd name? If so, offer a one-tap merge. Re-runs
  // when payment terms change (which decides the survivor). See vendorMatch.js.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!d.vendor || !window.kanjo?.procurement?.vendorMatch) { if (!cancelled) setDup(null); return; }
      try {
        const all = await window.kanjo.procurement.vendors.listActive();
        const other = window.kanjo.procurement.vendorMatch.bestVendorMatch(d.vendor.name, all, d.vendor.vendorId);
        if (!other) { if (!cancelled) setDup(null); return; }
        // Survivor = the vendor that already has payment terms; else the one with
        // more invoices; else this one. Keep the established record, fold in the rest.
        let survivorId = (other.paymentMethod && !d.vendor.paymentMethod) ? other.vendorId
          : (d.vendor.paymentMethod && !other.paymentMethod) ? d.vendor.vendorId
          : null;
        if (!survivorId) {
          const [cThis, cOther] = await Promise.all([
            window.kanjo.db.invoices.where('vendorId').equals(d.vendor.vendorId).count(),
            window.kanjo.db.invoices.where('vendorId').equals(other.vendorId).count(),
          ]);
          survivorId = cOther > cThis ? other.vendorId : d.vendor.vendorId;
        }
        if (!cancelled) setDup({ other, survivorId, busy: false });
      } catch { if (!cancelled) setDup(null); }
    })();
    return () => { cancelled = true; };
  }, [d.vendor && d.vendor.vendorId, d.vendor && d.vendor.name, method]);

  const flashSaved = () => { setSaved(true); setTimeout(() => setSaved(false), 1600); };
  const saveName = async () => {
    const trimmed = name.trim();
    if (!d.vendor || !trimmed || trimmed === d.vendor.name) return;
    // The field is showing a fictional name; saving it would rename the real
    // supplier to the fake. Renaming is an operating action, not part of a
    // walkthrough — so demo mode simply does not rename.
    if (window.kanjo?.demo?.isOn?.()) return;
    try {
      // Keep the previous (often OCR-garbled) name as an alias so a future scan
      // that OCRs the same way still maps to this vendor instead of duplicating.
      const aliases = [...(d.vendor.aliases || []), d.vendor.name].filter(Boolean);
      await window.kanjo.procurement.vendors.update(d.vendor.vendorId, { name: trimmed, aliases });
      d.vendor.name = trimmed;
      d.vendor.aliases = aliases;
      flashSaved();
    } catch (e) { console.warn('[VendorDetail] rename failed', e); }
  };
  const savePolicy = async (nextMethod, nextOffset) => {
    if (!d.vendor) return;
    setMethod(nextMethod);
    if (nextOffset != null) setOffset(nextOffset);
    const periodic = nextMethod === 'statement' || nextMethod === 'monthly';
    try {
      await window.kanjo.procurement.vendors.update(d.vendor.vendorId, {
        paymentMethod: nextMethod || undefined,
        statementPayOffsetDays: periodic ? (nextOffset ?? offset) : undefined,
      });
      d.vendor.paymentMethod = nextMethod || undefined; // reflect locally for the header label
      try { await window.kanjo.alerts.dismissNewVendor(d.vendor.vendorId); } catch { /* non-fatal */ }
      flashSaved();
    } catch (e) { console.warn('[VendorDetail] save policy failed', e); }
  };
  const saveIdentify = async (by, label) => {
    if (!d.vendor) return;
    setMatchBy(by); setMatchLabel(label || '');
    try {
      await window.kanjo.procurement.vendors.update(d.vendor.vendorId, {
        paymentMatch: by || undefined,
        paymentLabel: by === 'label' ? (label || '').trim() || undefined : undefined,
      });
      d.vendor.paymentMatch = by || undefined;
      d.vendor.paymentLabel = by === 'label' ? (label || '').trim() : undefined;
      flashSaved();
    } catch (e) { console.warn('[VendorDetail] save identify failed', e); }
  };
  // Save order contacts on blur. Either field may be blank.
  const saveContacts = async () => {
    if (!d.vendor) return;
    const sms = orderSms.trim(), email = orderEmail.trim();
    if (sms === (d.vendor.orderSms || '') && email === (d.vendor.orderEmail || '')) return;
    try {
      await window.kanjo.procurement.vendors.update(d.vendor.vendorId, { orderSms: sms || undefined, orderEmail: email || undefined });
      d.vendor.orderSms = sms || undefined;
      d.vendor.orderEmail = email || undefined;
      flashSaved();
    } catch (e) { console.warn('[VendorDetail] save contacts failed', e); }
  };

  // A merged-away or stale vendor id is a dead end (e.g. pressing Back onto a
  // just-merged vendor). Bounce to the vendor list, REPLACING this history entry
  // so Back doesn't loop straight back onto the missing vendor.
  React.useEffect(() => {
    if (!d.notFound) return;
    window.history.replaceState(null, '', '#/buy/vendor');
    window.dispatchEvent(new Event('hashchange'));
  }, [d.notFound]);

  // Merge this vendor into `dstId` (the survivor) and land on it, REPLACING this
  // now-deleted vendor's history entry so Back doesn't hit the merged-away record.
  const mergeInto = async (dstId) => {
    if (!d.vendor || !dstId || dstId === d.vendor.vendorId) return;
    await window.kanjo.procurement.vendors.merge(d.vendor.vendorId, dstId);
    window.history.replaceState(null, '', `#/buy/vendor/detail?id=${encodeURIComponent(dstId)}`);
    window.dispatchEvent(new Event('hashchange'));
  };

  const doMerge = async () => {
    if (!dup || !d.vendor) return;
    const dstId = dup.survivorId;
    const srcId = dstId === d.vendor.vendorId ? dup.other.vendorId : d.vendor.vendorId;
    setDup((x) => x ? { ...x, busy: true } : x);
    try {
      // Survivor may be EITHER vendor (auto-picked by terms/invoice count), so
      // call merge directly here rather than through mergeInto (which always
      // treats the current vendor as the source).
      await window.kanjo.procurement.vendors.merge(srcId, dstId);
      window.history.replaceState(null, '', `#/buy/vendor/detail?id=${encodeURIComponent(dstId)}`);
      window.dispatchEvent(new Event('hashchange'));
      setDup(null);
    } catch (e) {
      console.warn('[VendorDetail] merge failed', e);
      const msg = e?.code === 'READ_ONLY'
        ? 'This device is read-only — open Kanjo on your main device to merge.'
        : "Couldn't merge right now — please try again.";
      setDup((x) => x ? { ...x, busy: false, error: msg } : x);
    }
  };

  // Manual merge: pick any other vendor to fold THIS one into (for duplicates the
  // auto-detector doesn't flag).
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [mergeErr, setMergeErr] = React.useState(null);
  const doManualMerge = async (dstId) => {
    setMergeErr(null);
    try {
      await mergeInto(dstId);
      setMergeOpen(false);
    } catch (e) {
      console.warn('[VendorDetail] manual merge failed', e);
      setMergeErr(e?.code === 'READ_ONLY'
        ? 'This device is read-only — open Kanjo on your main device to merge.'
        : "Couldn't merge right now — please try again.");
    }
  };

  if (d.loading) return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <KHeader back title="Vendor" />
      <div style={{ padding: 24, color: 'var(--on-dim)' }}>Loading…</div>
    </KShell>
  );
  if (!d.vendor) return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <KHeader back title="Vendor" />
      <div style={{ padding: 24, color: 'var(--on-dim)' }}>
        {d.notFound ? 'This vendor was merged or removed — taking you back to your vendors…' : 'No vendors yet. Scan a receipt to add one.'}
      </div>
    </KShell>
  );

  const m = d.metrics;
  // 90-day window for the average (from the invoices we loaded).
  const cutoff = (() => { const t = new Date(); t.setUTCDate(t.getUTCDate() - 90); return t.toISOString().slice(0, 10); })();
  const win90 = (invoices || []).filter((i) => (i.issuedDate || '') >= cutoff);
  const spend90 = win90.reduce((s, i) => s + (i.totalCents || 0), 0) || m.totalSpendCents || 0;
  const avgCents = win90.length ? Math.round(spend90 / win90.length) : 0;
  const D = (c) => fmtMoneyV(c, d.currency, d.locale);
  const payFn = window.kanjo?.cashflow?.payments?.invoicePayment;

  // Card vendors → this-month credit-card spend; everyone else → outstanding bills due.
  const ym0 = localMonth();
  const ccSpendCents = method === 'card'
    ? (invoices || []).filter((i) => (i.issuedDate || '').startsWith(ym0)).reduce((s, i) => s + (i.totalCents || 0), 0)
    : 0;
  const billsDueCents = method !== 'card' && payFn
    ? (invoices || []).reduce((s, i) => s + ((payFn(i, d.vendor)?.paid ? 0 : (i.totalCents || 0))), 0)
    : 0;

  // mm/dd/yyyy formatter for the Last-order tile.
  const fmtMdy = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : '—'; };

  // Statement / monthly vendors are paid one lump per cycle, so group the unpaid
  // invoices by the statement cycle payFn assigns (its due date) and surface a
  // per-cycle subtotal — that lump IS what's owed on each statement date. Paid
  // invoices follow in their own group. Other payment methods keep a flat list.
  const isStatement = method === 'statement' || method === 'monthly';
  const invoiceGroups = (() => {
    const list = (invoices || []).slice(0, 30);
    if (!list.length) return [];
    const rows = list.map((inv) => ({ inv, p: payFn ? payFn(inv, d.vendor) : { paid: false } }));
    if (!isStatement) return [{ key: 'all', rows }];
    const cycles = new Map(); const paid = [];
    for (const r of rows) {
      if (r.p.paid) { paid.push(r); continue; }
      const k = r.p.expectedPayIso || 'unscheduled';
      if (!cycles.has(k)) cycles.set(k, []);
      cycles.get(k).push(r);
    }
    const groups = [];
    for (const [due, rs] of cycles) {
      groups.push({ key: due, due: due === 'unscheduled' ? null : due, total: rs.reduce((s, r) => s + (r.inv.totalCents || 0), 0), count: rs.length, rows: rs });
    }
    if (paid.length) groups.push({ key: 'paid', paid: true, total: paid.reduce((s, r) => s + (r.inv.totalCents || 0), 0), count: paid.length, rows: paid });
    return groups;
  })();

  const termLabel = method ? PAYMENT_LABEL[method] : 'Terms not set';
  const identifyLabel = matchBy === 'amount' ? 'Matched by invoice amount'
    : matchBy === 'label' ? `Matched by label "${matchLabel}"`
    : 'Not set';

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back title="Vendor" right={<svg width="20" height="20" viewBox="0 0 20 20" fill="var(--on-soft)"><circle cx="4" cy="10" r="1.6"/><circle cx="10" cy="10" r="1.6"/><circle cx="16" cy="10" r="1.6"/></svg>}/>

        {/* Possible duplicate — a near-identical vendor exists under another name. */}
        {dup && (() => {
          const survivorName = vendorName(dup.survivorId === d.vendor.vendorId ? d.vendor.name : dup.other.name);
          return (
            <div style={{ margin: '0 20px 14px', padding: '12px 14px',
                          background: 'var(--surface-3, #f0f0f3)', borderRadius: 12,
                          border: '1px solid var(--warning, #c9941f)' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', lineHeight: 1.45 }}>
                Looks like a duplicate of <strong style={{ color: 'var(--ink)' }}>{vendorName(dup.other.name)}</strong>. Merging combines their invoices &amp; price history and keeps “{survivorName}”.
              </div>
              <button type="button" onClick={doMerge} disabled={dup.busy}
                style={{ marginTop: 10, padding: '8px 16px', borderRadius: 9, border: 'none',
                         background: 'var(--accent,#5b8def)', color: '#fff', fontSize: 13, fontWeight: 600,
                         cursor: dup.busy ? 'default' : 'pointer', opacity: dup.busy ? 0.6 : 1 }}>
                {dup.busy ? 'Merging…' : 'Merge'}
              </button>
              {dup.error && (
                <ErrorBanner style={{ marginTop: 8, fontSize: 12.5 }}>{dup.error}</ErrorBanner>
              )}
            </div>
          );
        })()}

        {/* Vendor header card — avatar + name + terms + metrics row */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: 'var(--brand-soft)', color: 'var(--brand)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 600, flexShrink: 0,
              }}>{vendorInitial(vendorName(d.vendor.name))}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  aria-label="Vendor name" placeholder="Vendor name"
                  style={{
                    width: '100%', border: 'none', background: 'transparent', padding: 0,
                    fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)', fontFamily: 'var(--sans)',
                  }}
                />
                <div style={{ fontSize: 13, color: method ? 'var(--ink-3)' : 'var(--warning)', marginTop: 2 }}>
                  {termLabel}
                  {d.vendor.deliveryDays && d.vendor.deliveryDays.length ? ` · Delivers ${d.vendor.deliveryDays.join(', ')}` : ''}
                </div>
              </div>
            </div>
            {/* Smaller spend · avg order · last order */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--rule)', display: 'flex', gap: 12 }}>
              <VendorMetric label="Spend · 90d" value={D(spend90)} />
              <VendorMetric label="Avg order · 90d" value={win90.length ? D(avgCents) : '—'} />
              <VendorMetric label="Last order" value={fmtMdy(m.lastOrderDate)} mono={false} last />
            </div>
            {/* Bills due (terms vendors) or this-month credit-card spend (card vendors).
                Hidden entirely when there's nothing owed — "None" is just noise. */}
            {(method === 'card' ? ccSpendCents > 0 : billsDueCents > 0) && (
              <div style={{ marginTop: 10, display: 'flex', gap: 12 }}>
                {method === 'card' ? (
                  <VendorMetric label={`CC spent · ${ym0.slice(5)}/${ym0.slice(2,4)}`} value={D(ccSpendCents)} last />
                ) : (
                  <VendorMetric label="Bills due" value={D(billsDueCents)} last />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Weekly spend chart — 13 ISO weeks (Mon→Sun), current week faded. */}
        <VendorWeeklySpend invoices={invoices} currency={d.currency} locale={d.locale} />

        {/* Two settings buttons → modals */}
        <div style={{ padding: '0 20px 14px', display: 'grid', gap: 10 }}>
          <VendorSettingRow title="How you pay this vendor" value={termLabel} warn={!method} onClick={() => setModal('pay')} />
          {/* Credit-card vendors aren't payment-tracked, so identification doesn't apply. */}
          {method !== 'card' && (
            <VendorSettingRow title="How payments are identified" value={identifyLabel} warn={!matchBy} onClick={() => setModal('identify')} />
          )}
          <VendorSettingRow title="Merge into another vendor" value="Combine this vendor's invoices & history into another" onClick={() => { setMergeErr(null); setMergeOpen(true); }} />
          {saved && <div style={{ fontSize: 12, color: 'var(--positive)', paddingLeft: 4 }}>Saved ✓</div>}
        </div>

        {/* Order contacts — where Send-cart in the shopping list will text/email to. */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Order contacts</div>
          <div className="card" style={{ padding: 14 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <input type="tel" value={orderSms} onChange={(e) => setOrderSms(e.target.value)} onBlur={saveContacts}
                placeholder="Text orders to (phone number)" aria-label="Order text number" inputMode="tel"
                style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}/>
              <input type="email" value={orderEmail} onChange={(e) => setOrderEmail(e.target.value)} onBlur={saveContacts}
                placeholder="Email orders to (address)" aria-label="Order email address" inputMode="email"
                style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}/>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8 }}>Used by Send cart in Buy → shopping list. Saved when you tap out of a field.</div>
          </div>
        </div>

        {/* Invoices list — tap a row to open the receipt detail. */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Invoices</div>
          <div className="card" style={{ padding: '4px 14px' }}>
            {invoices == null ? (
              <div style={{ padding: '12px 0', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
            ) : invoices.length === 0 ? (
              <div style={{ padding: '12px 0', color: 'var(--ink-3)', fontSize: 13 }}>No invoices yet.</div>
            ) : invoiceGroups.map((g, gi) => (
              <React.Fragment key={g.key}>
                {(g.due || g.paid) && (
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                                padding: '10px 2px 7px', borderBottom: '1px solid var(--hairline)' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                                   color: g.paid ? 'var(--ink-3)' : 'var(--ink-2)' }}>
                      {g.paid ? 'Paid' : `Due ${fmtMdy(g.due)}`}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: g.paid ? 'var(--ink-3)' : 'var(--ink)' }}>
                      {D(g.total)}{!g.paid && g.count > 1 ? ` · ${g.count} invoices` : ''}
                    </span>
                  </div>
                )}
                {g.rows.map(({ inv, p }, ri) => {
                  const lastOverall = gi === invoiceGroups.length - 1 && ri === g.rows.length - 1;
                  return (
                    <KLink key={inv.invoiceId} to={`/receipts/detail?id=${encodeURIComponent(inv.invoiceId)}`} asButton style={{ display: 'block' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: lastOverall ? 'none' : '1px solid var(--hairline)' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{D(inv.totalCents)}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{fmtMdy(inv.issuedDate)}{inv.invoiceNumber ? ` · #${inv.invoiceNumber}` : ''}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(!isStatement || p.paid) && (
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 7,
                              color: p.paid ? 'var(--positive)' : 'var(--warning,#9a6700)',
                              background: p.paid ? 'var(--positive-soft,#e7f6ee)' : 'var(--warning-soft,#fff4e0)' }}>
                              {p.paid ? 'Paid' : (p.expectedPayIso ? `Due ${fmtMdy(p.expectedPayIso)}` : 'Unpaid')}
                            </span>
                          )}
                          <Chev/>
                        </div>
                      </div>
                    </KLink>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {modal === 'pay' && (
        <VendorPayModal method={method} offset={offset} setOffset={setOffset}
          onSelect={savePolicy} onClose={() => setModal(null)} />
      )}
      {modal === 'identify' && (
        <VendorIdentifyModal matchBy={matchBy} matchLabel={matchLabel}
          onSave={(by, label) => { saveIdentify(by, label); setModal(null); }} onClose={() => setModal(null)} />
      )}
      {mergeOpen && (
        <VendorMergeModal currentId={d.vendor.vendorId} currentName={vendorName(d.vendor.name)} error={mergeErr}
          onPick={doManualMerge} onClose={() => setMergeOpen(false)} />
      )}
    </KShell>
  );
}

// ── "Merge into another vendor" modal ────────────────────────────────────────
// Pick the vendor to KEEP; this one's invoices, price history, and aliases fold
// into it and this record is removed. For duplicates the auto-detector misses
// (e.g. a short acronym vs its full name).
function VendorMergeModal({ currentId, currentName, error, onPick, onClose }) {
  const [vendors, setVendors] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [confirmId, setConfirmId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await window.kanjo.procurement.vendors.listActive();
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setVendors(list.filter((v) => v.vendorId !== currentId));
      } catch { if (!cancelled) setVendors([]); }
    })();
    return () => { cancelled = true; };
  }, [currentId]);

  const needle = q.trim().toLowerCase();
  const shown = (vendors || []).filter((v) => !needle || (v.name || '').toLowerCase().includes(needle));
  const confirmVendor = (vendors || []).find((v) => v.vendorId === confirmId);

  const pick = async (id) => { setBusy(true); try { await onPick(id); } finally { setBusy(false); } };

  return (
    <VendorSheet title="Merge into another vendor" onClose={onClose}>
      {confirmVendor ? (
        <div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
            Move all of <strong style={{ color: 'var(--ink)' }}>{currentName}</strong>'s invoices and price
            history into <strong style={{ color: 'var(--ink)' }}>{vendorName(confirmVendor.name)}</strong>, and remove
            “{currentName}”. This can't be undone.
          </div>
          {error && <ErrorBanner style={{ fontSize: 12.5, marginBottom: 12 }}>{error}</ErrorBanner>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => pick(confirmVendor.vendorId)} disabled={busy}
              style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: 'var(--accent,#5b8def)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Merging…' : `Merge into ${vendorName(confirmVendor.name)}`}
            </button>
            <button onClick={() => setConfirmId(null)} disabled={busy}
              style={{ padding: '11px 16px', borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink-2)', fontSize: 14, cursor: 'pointer' }}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
            Pick the vendor to keep. “{currentName}” folds into it.
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your vendors"
            aria-label="Search vendors"
            style={{ width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', marginBottom: 10 }}/>
          <div style={{ display: 'grid', gap: 6 }}>
            {vendors == null && <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 2px' }}>Loading…</div>}
            {vendors != null && shown.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 2px' }}>No other vendors to merge into.</div>
            )}
            {shown.map((v) => (
              <button key={v.vendorId} onClick={() => setConfirmId(v.vendorId)}
                style={{ textAlign: 'left', padding: '11px 12px', borderRadius: 10, cursor: 'pointer',
                         border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14, fontWeight: 600 }}>
                {vendorName(v.name)}
              </button>
            ))}
          </div>
        </div>
      )}
    </VendorSheet>
  );
}

// ── Small metric cell (spend / avg / last order) ─────────────────────────────
function VendorMetric({ label, value, mono = true, last }) {
  return (
    <div style={{ flex: 1, borderRight: last ? 'none' : '1px solid var(--rule)', paddingRight: last ? 0 : 8 }}>
      <div className="label">{label}</div>
      <div className={mono ? 'mono' : ''} style={{ fontSize: 15, fontWeight: 600, marginTop: 4, color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

// ── A tappable settings row that opens a modal ───────────────────────────────
function VendorSettingRow({ title, value, warn, onClick }) {
  return (
    <button onClick={onClick} className="card" style={{
      width: '100%', textAlign: 'left', padding: '13px 16px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: '1px solid var(--rule-2)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{title}</div>
        <div style={{ fontSize: 12.5, color: warn ? 'var(--warning)' : 'var(--ink-3)', marginTop: 2 }}>{value}</div>
      </div>
      <Chev />
    </button>
  );
}

// ── Bottom-sheet modal scaffold ──────────────────────────────────────────────
function VendorSheet({ title, onClose, children }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480, background: 'var(--surface)', color: 'var(--ink)',
        borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 18px calc(env(safe-area-inset-bottom,0) + 20px)',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// ── "How you pay this vendor" modal ──────────────────────────────────────────
function VendorPayModal({ method, offset, setOffset, onSelect, onClose }) {
  const options = [
    ['card', 'Credit card', 'Paid at purchase — never shows as unpaid'],
    ['check_on_delivery', 'Check on delivery', 'Paid on the delivery date'],
    ['statement', 'Statement (semi-monthly)', 'Billed 1–15 & 16–end, paid after each close'],
    ['monthly', 'Monthly statement', 'Billed by month, paid after month-end'],
    ['terms', 'On account / net terms', 'Stays unpaid until you mark it paid'],
  ];
  const periodic = method === 'statement' || method === 'monthly';
  return (
    <VendorSheet title="How you pay this vendor" onClose={onClose}>
      <div style={{ display: 'grid', gap: 8 }}>
        {options.map(([val, label, sub]) => (
          <button key={val} onClick={() => onSelect(val, offset)} style={{
            textAlign: 'left', padding: '11px 12px', borderRadius: 10, cursor: 'pointer',
            border: method === val ? '1.5px solid var(--brand)' : '1px solid var(--rule-2)',
            background: method === val ? 'var(--brand-soft)' : 'var(--surface)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{label}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{sub}</div>
          </button>
        ))}
      </div>
      {periodic && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Pay</span>
          <input inputMode="numeric" value={String(offset)} aria-label="Days after period close"
            onChange={(e) => setOffset(Number(e.target.value) || 0)}
            onBlur={() => onSelect(method, offset)}
            style={{ width: 56, textAlign: 'right', border: '1px solid var(--rule-2)', borderRadius: 8, padding: '6px 8px', fontFamily: 'var(--mono)', background: 'var(--surface)', color: 'var(--ink)' }}/>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>days after each period closes</span>
        </div>
      )}
      <button onClick={onClose} style={{ width: '100%', marginTop: 16, padding: '11px', borderRadius: 10, border: 'none', background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Done</button>
    </VendorSheet>
  );
}

// ── "How payments are identified" modal ──────────────────────────────────────
// Mirrors the cashflow tab's transaction matching: either match a bank debit by
// the invoice's amount (check on delivery), or by a text label that a payment
// service stamps on the bank transaction.
function VendorIdentifyModal({ matchBy, matchLabel, onSave, onClose }) {
  const [by, setBy] = React.useState(matchBy || 'amount');
  const [label, setLabel] = React.useState(matchLabel || '');
  return (
    <VendorSheet title="How payments are identified" onClose={onClose}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
        How Kanjo recognizes this vendor's payment in your bank feed.
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <button onClick={() => setBy('amount')} style={vendorOptStyle(by === 'amount')}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>By invoice amount</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>For check-on-delivery — the bank debit clears for the invoice total.</div>
        </button>
        <button onClick={() => setBy('label')} style={vendorOptStyle(by === 'label')}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>By label</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>For payments via a payment service — match the descriptor on the bank transaction.</div>
        </button>
      </div>
      {by === 'label' && (
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. BILL.COM SYSCO"
          aria-label="Bank transaction label"
          style={{ width: '100%', marginTop: 12, padding: '11px 12px', borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }}/>
      )}
      <button onClick={() => onSave(by, label)} disabled={by === 'label' && !label.trim()}
        style={{ width: '100%', marginTop: 16, padding: '11px', borderRadius: 10, border: 'none', background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (by === 'label' && !label.trim()) ? 0.6 : 1 }}>Save</button>
    </VendorSheet>
  );
}
function vendorOptStyle(on) {
  return { textAlign: 'left', padding: '11px 12px', borderRadius: 10, cursor: 'pointer',
    border: on ? '1.5px solid var(--brand)' : '1px solid var(--rule-2)', background: on ? 'var(--brand-soft)' : 'var(--surface)' };
}

// ─── Vendor list — all active vendors; tap one for its detail/payment terms ──
const PAYMENT_LABEL = {
  card: 'Credit card',
  check_on_delivery: 'Check on delivery',
  statement: 'Statement (semi-monthly)',
  monthly: 'Monthly statement',
  terms: 'On account / net terms',
};

function VendorList({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true, vendors: [] });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, vendors: [] }); return; }
        const vendors = await window.kanjo.procurement.vendors.listActive();
        vendors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setSt({ loading: false, vendors });
      } catch (e) {
        console.warn('[VendorList] load failed', e);
        if (!cancelled) setSt({ loading: false, vendors: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title="Vendors" subtitle="Tap a supplier for details & payment terms" />
        <div style={{ padding: '0 16px' }}>
          {st.loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>Loading…</div>}
          {!st.loading && st.vendors.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>No vendors yet. Scan a receipt to add one.</div>
          )}
          {st.vendors.map((v) => (
            <KLink key={v.vendorId} to={`/buy/vendor/detail?id=${encodeURIComponent(v.vendorId)}`} asButton style={{ display: 'block' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 4px', borderBottom: '1px solid var(--hairline)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div aria-hidden="true" style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: 'var(--brand-soft)', color: 'var(--brand)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600,
                  }}>{vendorInitial(vendorName(v.name))}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vendorName(v.name)}</div>
                    <div style={{ fontSize: 12.5, color: v.paymentMethod ? 'var(--on-dim)' : 'var(--warning)' }}>
                      {v.paymentMethod ? PAYMENT_LABEL[v.paymentMethod] : 'Set payment terms →'}
                    </div>
                  </div>
                </div>
                <Chev/>
              </div>
            </KLink>
          ))}
        </div>
      </div>
    </KShell>
  );
}

// ── /buy/new-vendor — compare a prospective supplier's price sheet ──────────
// Upload a PDF/photo of a new vendor's product list → OCR → Claude matches it
// against what we actively buy → report the gaps (items they don't carry) and
// price deltas where both prices are known.
function downscaleVendorJpeg(file, maxDim = 2000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('downscale failed'))), 'image/jpeg', quality);
    };
    img.onerror = () => reject(new Error('image load failed'));
    img.src = URL.createObjectURL(file);
  });
}

const STAGE_COPY = {
  gathering: 'Checking what you currently buy…',
  uploading: 'Uploading the price sheet…',
  reading: 'Reading the products off the sheet…',
  matching: 'Matching against what you buy…',
};

function deltaLabel(pct) {
  const abs = Math.abs(pct);
  const n = Number.isInteger(abs) ? abs : abs.toFixed(1);
  return pct < 0 ? `${n}% cheaper` : pct > 0 ? `${n}% pricier` : 'same price';
}

function NewVendor({ dark = false }) {
  const nav = React.useContext(KanjoNav);
  const fileRef = React.useRef(null);
  const [stage, setStage] = React.useState('pick'); // pick | uploading | reading | matching | done | error
  const [error, setError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const isPdf = file.type === 'application/pdf';
    const isImg = /^image\//.test(file.type);
    if (!isPdf && !isImg) { setError('Choose a PDF or a photo of the price sheet.'); setStage('error'); return; }
    setError(null); setResult(null); setStage('uploading');
    try {
      const blob = isPdf ? file : await downscaleVendorJpeg(file);
      const mime = isPdf ? 'application/pdf' : 'image/jpeg';
      const out = await window.kanjo.procurement.vendorCompare.compareNewVendorList(blob, mime, {
        onStage: (s) => setStage(s),
      });
      setResult(out); setStage('done');
    } catch (err) {
      const msg = err?.code === 'VENDORLIST_PAID_REQUIRED'
        ? 'Comparing a new vendor is part of a paid plan. Open Account to upgrade.'
        : err?.message || 'Something went wrong reading that file.';
      setError(msg); setStage('error');
    }
  };

  const processing = stage === 'gathering' || stage === 'uploading' || stage === 'reading' || stage === 'matching';

  return (
    <KShell tab="buy" noNav>
      <KHeader title="Compare a new vendor" onBack={() => nav.navigate('/buy')} />
      <div style={{ padding: '8px 20px 28px' }}>
        {processing ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--on-soft)' }}>
            <div className="spinner" style={{ margin: '0 auto 18px' }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on)' }}>{STAGE_COPY[stage]}</div>
            <div style={{ fontSize: 13, color: 'var(--on-soft)', marginTop: 6 }}>
              We never store the vendor's list — it's read once and matched against your purchases.
            </div>
          </div>
        ) : stage === 'done' && result ? (
          <NewVendorReport result={result} onAgain={() => { setStage('pick'); setResult(null); }} />
        ) : (
          <>
            <div style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 22 }}>
              Got a quote or product list from a <strong>new supplier</strong>? Upload it and we'll find which of the
              things you buy they <strong>don't carry</strong> — and, where prices are listed, whether they're
              cheaper or pricier than what you pay now.
            </div>
            {error && <ErrorBanner style={{ marginBottom: 14 }}>{error}</ErrorBanner>}
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
              onChange={onFile} style={{ display: 'none' }} />
            <button className="btn lg" onClick={() => fileRef.current?.click()}
              style={{ width: '100%', cursor: 'pointer' }}>
              Choose PDF or photo
            </button>
          </>
        )}
      </div>
    </KShell>
  );
}

function NewVendorReport({ result, onAgain }) {
  const { vendorName, report, vendorLineCount, ourItemCount } = result;
  const matches = report.matches || [];
  const missing = report.missing || [];
  // Cheapest wins first, then pricier, then no-price.
  const sortedMatches = [...matches].sort((a, b) => {
    const av = a.price ? a.price.deltaPct : Infinity;
    const bv = b.price ? b.price.deltaPct : Infinity;
    return av - bv;
  });

  const [shareMsg, setShareMsg] = React.useState('');
  const onShare = async () => {
    const text = window.kanjo.procurement.vendorCompareMath.formatReportText(result);
    const title = `${vendorName || 'New vendor'} — supplier comparison`;
    // Native share sheet on mobile (Mail, Messages/SMS, WhatsApp, …).
    try {
      if (navigator.share) { await navigator.share({ title, text }); return; }
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user dismissed the sheet — not an error
    }
    // Fallback (no Web Share API): copy to clipboard.
    try {
      await navigator.clipboard.writeText(text);
      setShareMsg('Copied to clipboard');
    } catch {
      setShareMsg('Sharing isn’t available on this device');
    }
    setTimeout(() => setShareMsg(''), 2600);
  };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--on)' }}>{vendorName || 'New vendor'}</div>
        <div style={{ fontSize: 13, color: 'var(--on-soft)', marginTop: 2 }}>
          Checked {ourItemCount} of your items against {vendorLineCount} products.
        </div>
      </div>

      {/* What they DO carry — shown first. */}
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div className="label" style={{ marginBottom: 8, color: 'var(--ink-2)' }}>
          They carry ({matches.length})
        </div>
        {matches.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>No overlap with what you buy.</div>
        ) : sortedMatches.map((m) => (
          <div key={m.itemId} style={{ padding: '9px 0', borderTop: '1px solid var(--rule)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontSize: 14, color: 'var(--ink)', minWidth: 0 }}>{m.ourName}</span>
              {m.price ? (
                <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: m.price.cheaper ? 'var(--positive-ink)' : 'var(--danger)' }}>
                  {deltaLabel(m.price.deltaPct)}
                </span>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>no price listed</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {m.vendorDescription}{typeof m.confidence === 'number' && m.confidence < 90 ? ` · ${m.confidence}% match` : ''}
            </div>
          </div>
        ))}
      </div>

      {/* What they DON'T carry — shown after. */}
      <div className="card" style={{ padding: 16, marginBottom: 18 }}>
        <div className="label" style={{ marginBottom: 8, color: 'var(--ink-2)' }}>
          Doesn't carry ({missing.length})
        </div>
        {missing.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>They carry everything you're buying right now.</div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 10 }}>
              You'd still need your current supplier for these:
            </div>
            {missing.map((m) => (
              <div key={m.itemId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: '1px solid var(--rule)' }}>
                <span style={{ fontSize: 14, color: 'var(--ink)' }}>{m.name}</span>
                {m.category && <span style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>{m.category}</span>}
              </div>
            ))}
          </>
        )}
      </div>

      {shareMsg && (
        <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--on-soft)', marginBottom: 8 }}>{shareMsg}</div>
      )}
      <button className="btn primary" onClick={onShare} style={{ width: '100%', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/>
        </svg>
        Share / export list
      </button>
      <button className="btn" onClick={onAgain} style={{ width: '100%' }}>Compare another list</button>
    </div>
  );
}

Object.assign(window, { VendorDetail, VendorList, KpiTile, NewVendor, NewVendorReport });
